import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';

// Log environment variables availability for debugging
console.log('EmailJS Environment Variables Status:', {
  PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY ? 'Available ✓' : 'Missing ✗',
  SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID ? 'Available ✓' : 'Missing ✗',
  TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID ? 'Available ✓' : 'Missing ✗',
});

// Create a validation function for EmailJS configuration
const validateEmailJSConfig = () => {
  const missingVars = [];
  
  if (!process.env.REACT_APP_EMAILJS_PUBLIC_KEY) missingVars.push('REACT_APP_EMAILJS_PUBLIC_KEY');
  if (!process.env.REACT_APP_EMAILJS_SERVICE_ID) missingVars.push('REACT_APP_EMAILJS_SERVICE_ID');
  if (!process.env.REACT_APP_EMAILJS_TEMPLATE_ID) missingVars.push('REACT_APP_EMAILJS_TEMPLATE_ID');
  
  return {
    isValid: missingVars.length === 0,
    missingVars
  };
};

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailJSConfigValid, setEmailJSConfigValid] = useState(true);
  
  // Initialize EmailJS only after component mounts to ensure environment variables are loaded
  useEffect(() => {
    try {
      const { isValid, missingVars } = validateEmailJSConfig();
      
      if (!isValid) {
        console.error('Missing EmailJS configuration variables:', missingVars);
        setEmailJSConfigValid(false);
        setStatus({
          type: 'error',
          message: 'Contact form is currently unavailable due to configuration issues. Please try again later or contact directly via email.'
        });
        return;
      }
      
      console.log('Initializing EmailJS with public key:', process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
      setEmailJSConfigValid(true);
    } catch (error) {
      console.error('Error initializing EmailJS:', error);
      setEmailJSConfigValid(false);
      setStatus({
        type: 'error',
        message: 'Contact form initialization failed. Please try again later or contact directly via email.'
      });
    }
  }, []);

  const onSubmit = async (data) => {
    // Skip if configuration is invalid
    if (!emailJSConfigValid) {
      setStatus({
        type: 'error',
        message: 'Contact form is unavailable due to configuration issues. Please email directly.'
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    
    try {
      // First, validate configuration again as a safeguard
      const { isValid, missingVars } = validateEmailJSConfig();
      if (!isValid) {
        throw new Error(`Missing configuration: ${missingVars.join(', ')}`);
      }
      
      // Create the template parameters object exactly as expected by EmailJS
      const templateParams = {
        from_name: data.name,
        from_email: data.email,
        subject: data.subject,
        message: data.message,
        to_name: 'Gerald Suleh',  // Add recipient name for template
        reply_to: data.email,     // Add reply-to email
        // Additional parameters that might be expected by your template
        user_name: data.name,
        user_email: data.email,
        contact_number: '',
        message_html: data.message.replace(/\n/g, '<br />'),
        date: new Date().toISOString()
      };

      // Validate template parameters
      const requiredParams = ['from_name', 'from_email', 'message'];
      const missingParams = requiredParams.filter(param => !templateParams[param]);
      
      if (missingParams.length > 0) {
        throw new Error(`Missing required parameters: ${missingParams.join(', ')}`);
      }
      
      console.log('EmailJS Configuration:', {
        PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY ? '[MASKED]' : 'MISSING',
        SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
        TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID
      });
      
      console.log('Sending email with params:', JSON.stringify(templateParams, null, 2));  // Detailed debug log

      // Send the email with a timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout after 15 seconds')), 15000)
      );
      
      const sendPromise = emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      const response = await Promise.race([sendPromise, timeoutPromise]);
      
      console.log('EmailJS complete response:', JSON.stringify(response, null, 2));  // Detailed debug log

      // Handle response status
      if (response.status === 200 || response.status === 'OK') {
        console.log('Email sent successfully with text:', response.text);
        setStatus({ 
          type: 'success', 
          message: 'Thank you! Your message has been sent successfully.' 
        });
        reset();
      } else {
        throw new Error(`Unexpected response status: ${response.status}, text: ${response.text || 'No additional information'}`);
      }
    } catch (error) {
      console.error('EmailJS detailed error:', error);
      
      // Categorize errors for better user feedback
      let errorMessage = 'An unexpected error occurred. Please try again later.';
      
      if (error.message.includes('Missing configuration')) {
        errorMessage = 'The contact form is misconfigured. Please email me directly instead.';
        console.error('Configuration error:', error.message);
      } else if (error.message.includes('Missing required parameters')) {
        errorMessage = 'Please ensure all required fields are filled correctly.';
        console.error('Parameter error:', error.message);
      } else if (error.message.includes('timeout')) {
        errorMessage = 'The request timed out. Please check your network connection and try again.';
        console.error('Timeout error:', error.message);
      } else if (error.name === 'NetworkError' || error.message.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
        console.error('Network error:', error.message);
      } else if (error.message.includes('Unexpected response')) {
        errorMessage = 'The server returned an unexpected response. Please try again or contact directly via email.';
        console.error('Response error:', error.message);
      }
      
      setStatus({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: 'Phone',
      info: '+254 706 141117',
      link: 'tel:+254706141117',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Email',
      info: 'grldsuleh@gmail.com',
      link: 'mailto:grldsuleh@gmail.com',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Location',
      info: 'Nairobi, Kenya',
      link: 'https://maps.google.com/?q=Nairobi,Kenya',
    },
  ];

  return (
    <section ref={ref} className="py-20 bg-secondary-50 dark:bg-secondary-800">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-4">
            Get In Touch
          </h2>
          <p className="text-secondary-600 dark:text-secondary-300 max-w-2xl mx-auto">
            Interested in cybersecurity consulting, SOC advisory, AI-driven security solutions, speaking engagements, or community initiatives? Reach out using the contact information or form below.
          </p>
          <div className="w-20 h-1 bg-primary-600 mx-auto mt-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white dark:bg-secondary-700 rounded-lg shadow-custom p-8">
              <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6">
                Send Me a Message
              </h3>
              
              {status.message && (
                <div className={`mb-6 p-4 rounded ${
                  status.type === 'success' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
                    : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                }`}>
                  {status.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit(onSubmit)} className={emailJSConfigValid ? '' : 'opacity-75 pointer-events-none'}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-secondary-700 dark:text-secondary-300 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    className={`w-full px-4 py-3 rounded-md border ${errors.name ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-800 text-secondary-800 dark:text-white`}
                    placeholder="Your name"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && (
                    <p className="mt-1 text-red-500 text-sm">{errors.name.message}</p>
                  )}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="email" className="block text-secondary-700 dark:text-secondary-300 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    className={`w-full px-4 py-3 rounded-md border ${errors.email ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-800 text-secondary-800 dark:text-white`}
                    placeholder="Your email address"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                  {errors.email && (
                    <p className="mt-1 text-red-500 text-sm">{errors.email.message}</p>
                  )}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-secondary-700 dark:text-secondary-300 mb-2">Subject</label>
                  <select
                    id="subject"
                    className={`w-full px-4 py-3 rounded-md border ${errors.subject ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-800 text-secondary-800 dark:text-white`}
                    {...register('subject', { required: 'Please select a subject' })}
                    defaultValue=""
                  >
                    <option value="" disabled>Select a subject</option>
                    <option value="SOC Consulting & Advisory">Information Security Engineering Consulting & Advisory</option>
                    <option value="VAPT / Penetration Testing">VAPT / Penetration Testing</option>
                    <option value="AI-Driven Security Solutions">AI-Driven Security Solutions</option>
                    <option value="Backend Development Project">Backend Development Project</option>
                    <option value="Cybersecurity Training & Workshop">Cybersecurity Training & Workshop</option>
                    <option value="Speaking Engagement / Panel">Speaking Engagement / Panel</option>
                    <option value="Research Collaboration">Research Collaboration</option>
                    <option value="Community Initiative">Community Initiative</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.subject && (
                    <p className="mt-1 text-red-500 text-sm">{errors.subject.message}</p>
                  )}
                </div>
                
                <div className="mb-6">
                  <label htmlFor="message" className="block text-secondary-700 dark:text-secondary-300 mb-2">Message</label>
                  <textarea
                    id="message"
                    rows="5"
                    className={`w-full px-4 py-3 rounded-md border ${errors.message ? 'border-red-500' : 'border-secondary-300 dark:border-secondary-600'} focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-800 text-secondary-800 dark:text-white`}
                    placeholder="Please provide details about your inquiry or project..."
                    {...register('message', { 
                      required: 'Message is required',
                      minLength: { value: 20, message: 'Message should be at least 20 characters' }
                    })}
                  ></textarea>
                  {errors.message && (
                    <p className="mt-1 text-red-500 text-sm">{errors.message.message}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : 'Send Message'}
                </button>
              </form>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white dark:bg-secondary-700 rounded-lg shadow-custom p-8 h-full">
              <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6">
                Contact Information
              </h3>
              
              <div className="space-y-6 mb-8">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="mr-4 p-3 bg-primary-100 dark:bg-primary-900 rounded-full text-primary-600 dark:text-primary-400">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-secondary-800 dark:text-white">
                        {item.title}
                      </h4>
                      {item.link ? (
                        <a 
                          href={item.link}
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                          target={item.link.startsWith('http') ? '_blank' : undefined}
                          rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                          {item.info}
                        </a>
                      ) : (
                        <p className="text-secondary-600 dark:text-secondary-300">
                          {item.info}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
                  <div>
                    <h4 className="text-lg font-medium text-secondary-800 dark:text-white mb-4">
                      Connect With Me
                    </h4>
                    <div className="flex space-x-4">
                      <a 
                        href="https://www.linkedin.com/in/gerald-suleh/" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-[#0077B5] text-white rounded-full hover:opacity-80 transition-opacity duration-300"
                        aria-label="LinkedIn"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </a>
                      
                      <a 
                        href="https://github.com/GSuleh" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-[#333] text-white rounded-full hover:opacity-80 transition-opacity duration-300"
                        aria-label="GitHub"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                      </a>
                    </div>
                    
                    <div className="mt-8 p-6 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                      <h5 className="font-medium text-primary-800 dark:text-primary-300 mb-2">Available For</h5>
                      <ul className="space-y-2">
                        {[
                          'Information Security Engineering Consulting & Advisory',
                          'VAPT / Penetration Testing',
                          'AI-Driven Security Solutions',
                          'Backend Development Projects',
                          'Cybersecurity Training & Workshops',
                          'Speaking Engagements & Panels',
                          'Research Collaboration',
                          'Community Engagement Initiatives'
                        ].map((item, i) => (
                          <li key={i} className="flex items-center text-secondary-700 dark:text-secondary-300">
                            <svg className="w-4 h-4 mr-2 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

